if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      constructor() {
        super();
        this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this);
      }

      // Children are queried here rather than in the constructor so the element also
      // works when it is upgraded by the parser before its subtree exists (quick add).
      init() {
        const sectionId = this.dataset.sectionId;

        this.form = this.closest('form');
        this.currentProductVariantId = this.dataset.productVariantId;

        // Control fields. The visible checkbox is decorative on this theme (the gift card
        // is always sent to a recipient), so the hidden `if_present` control is what tells
        // Shopify to treat the line item as a gift card recipient order.
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${sectionId}`);

        // Recipient fields. The `name` attributes must stay as Shopify's reserved
        // properties (`Recipient email`, `Recipient name`, `Message`, `Send on`) or
        // /cart/add rejects the request with a 422.
        this.nameInput = this.querySelector(`#Recipient-name-${sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${sectionId}`);
        this.send_onInput = this.querySelector(`#Recipient-send-on-${sectionId}`);
        this.sendAtInput = this.querySelector(`#Recipient-send-at-${sectionId}`);
        this.scheduleRadios = this.querySelectorAll('.form-radio');
        this.scheduleWrapper = this.querySelector('.time-date-selection');
        this.messageCounter = this.querySelector('.text-area-charac');

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;

        // Remember the Liquid-rendered fallback messages so a server-side error can be
        // undone without leaving its wording behind on the next client-side check.
        this.defaultFieldMessages = new Map();
        this.querySelectorAll('.recipient-fields .form__message .error-message').forEach((element) => {
          this.defaultFieldMessages.set(element, element.innerText);
        });

        if (this.checkboxInput) this.checkboxInput.disabled = true;
        if (this.hiddenControlField) this.hiddenControlField.disabled = false;

        if (this.offsetProperty) {
          this.offsetProperty.disabled = false;
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.setMinSendOnDate();
        this.syncScheduleFields();
        this.updateMessageCounter();

        this.bindFieldEvents();
      }

      bindFieldEvents() {
        this.scheduleRadios.forEach((radio) => {
          radio.addEventListener('change', () => this.syncScheduleFields());
        });

        this.nameInput?.addEventListener('blur', () => this.validateName());
        this.nameInput?.addEventListener('input', () => this.clearFieldError('name'));

        this.emailInput?.addEventListener('blur', () => this.validateEmailField());
        this.emailInput?.addEventListener('input', () => this.clearFieldError('email'));

        this.messageInput?.addEventListener('input', () => {
          this.updateMessageCounter();
          this.clearFieldError('message');
        });
        this.messageInput?.addEventListener('blur', () => this.validateMessage());

        this.send_onInput?.addEventListener('change', () => this.validateSendOn());
      }

      connectedCallback() {
        if (!this.initialized) {
          this.init();
          this.initialized = true;
        }

        // Capture on `document` so this runs before the `submit` listener that
        // <product-form> binds on the form itself, letting us block the /cart/add
        // request while a required recipient field is missing.
        document.addEventListener('submit', this.onFormSubmitCapture, true);

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onFormSubmitCapture, true);

        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
      }

      /* -------------------------------------------------- scheduling */

      setMinSendOnDate() {
        if (!this.send_onInput) return;

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');

        this.send_onInput.setAttribute('min', `${yyyy}-${mm}-${dd}`);
      }

      get isScheduled() {
        return Array.from(this.scheduleRadios).some((radio) => radio.checked && radio.value === 'מתוזמן');
      }

      // `Send on` and the companion time property are only submitted for a scheduled
      // delivery — an empty `Send on` would otherwise be sent on every add to cart.
      syncScheduleFields() {
        const scheduled = this.isScheduled;

        this.scheduleWrapper?.classList.toggle('active', scheduled);

        if (this.send_onInput) {
          this.send_onInput.disabled = !scheduled;
          if (!scheduled) this.send_onInput.value = '';
        }

        if (this.sendAtInput) {
          this.sendAtInput.disabled = !scheduled;
          if (!scheduled) this.sendAtInput.value = '';
        }

        if (!scheduled) this.clearFieldError('send_on');
      }

      updateMessageCounter() {
        if (!this.messageCounter || !this.messageInput) return;

        const max = this.messageInput.getAttribute('maxlength') || '';
        this.messageCounter.textContent = `${this.messageInput.value.length}/${max}`;
      }

      /* -------------------------------------------------- validation */

      validateEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());
      }

      setFieldError(key, hasError) {
        const errorElement = this.querySelector(`#RecipientForm-${key}-error-${this.dataset.sectionId}`);
        errorElement?.classList.toggle('hidden', !hasError);

        const inputElement = this[`${key}Input`];
        if (!inputElement) return;

        inputElement.setAttribute('aria-invalid', hasError ? 'true' : 'false');
        if (hasError) {
          inputElement.setAttribute('aria-describedby', `RecipientForm-${key}-error-${this.dataset.sectionId}`);
        } else {
          inputElement.removeAttribute('aria-describedby');
        }
      }

      clearFieldError(key) {
        this.setFieldError(key, false);
      }

      validateName() {
        const valid = !!this.nameInput && this.nameInput.value.trim() !== '';
        this.setFieldError('name', !valid);
        return valid;
      }

      validateEmailField() {
        const valid = !!this.emailInput && this.validateEmail(this.emailInput.value);
        this.setFieldError('email', !valid);
        return valid;
      }

      validateMessage() {
        const valid = !!this.messageInput && this.messageInput.value.trim() !== '';
        this.setFieldError('message', !valid);
        return valid;
      }

      validateSendOn() {
        if (!this.isScheduled) {
          this.clearFieldError('send_on');
          return true;
        }

        const valid = !!this.send_onInput && this.send_onInput.value !== '';
        this.setFieldError('send_on', !valid);
        return valid;
      }

      validate() {
        // Not short-circuited: every field should surface its own message at once.
        const results = [this.validateName(), this.validateEmailField(), this.validateMessage(), this.validateSendOn()];
        return results.every(Boolean);
      }

      onFormSubmitCapture(event) {
        if (!this.form || event.target !== this.form) return;
        if (this.validate()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.querySelector('[aria-invalid="true"]')?.focus();
      }

      /* -------------------------------------------------- errors & reset */

      clearInputFields() {
        [this.emailInput, this.nameInput, this.messageInput, this.send_onInput, this.sendAtInput].forEach((input) => {
          if (input) input.value = '';
        });
        this.updateMessageCounter();
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.hidden = false;

        if (typeof body === 'object' && body !== null) {
          if (this.errorMessage) this.errorMessage.innerText = this.defaultErrorHeader;

          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const placeholderElement = this.querySelector(fieldSelector);
            const label = placeholderElement?.getAttribute('placeholder') || key;
            const message = `${label} ${value}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        if (this.errorMessage) this.errorMessage.innerText = body || title;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = true;
        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
        });

        this.defaultFieldMessages?.forEach((text, element) => {
          element.innerText = text;
        });

        [this.emailInput, this.messageInput, this.nameInput, this.send_onInput].forEach((inputElement) => {
          if (!inputElement) return;
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        this.clearInputFields();
        this.clearErrorMessage();

        const immediateRadio = Array.from(this.scheduleRadios).find((radio) => radio.value === 'מיידי');
        if (immediateRadio) immediateRadio.checked = true;
        this.syncScheduleFields();
      }
    }
  );
}
